import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, ShieldCheck, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../lib/auth-context';

const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (d.message) return d.message;
  if (Array.isArray(d)) return d.map((e) => e.msg).join(' ');
  return fallback;
};

/**
 * V20 — Phone-verification gate. Learners must verify their number ONCE (WhatsApp
 * OTP) before they can use the portal — on every login path (password, Google,
 * payment-link auto-login). Verified once → never shown again. The number can be
 * changed here before sending the code.
 */
export default function PhoneVerifyGate() {
  const { user, refreshUser, logout } = useAuth();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [inUse, setInUse] = useState(null);

  const open = !!user && user.role === 'learner' && user.needs_phone_verification === true;

  useEffect(() => { if (open) setPhone((user?.phone || '').replace(/^\+?91(?=\d{10}$)/, '')); }, [open, user?.phone]);
  useEffect(() => {
    if (!cooldown) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!open) return null;

  const digits = phone.replace(/\D/g, '');
  const e164 = digits.length === 10 ? `+91${digits}` : (digits.length > 10 ? `+${digits}` : '');

  const send = async () => {
    if (!e164) { toast.error('Enter a valid mobile number'); return; }
    setBusy(true);
    try {
      await api.post('/api/auth/send-otp', { phone: e164 });
      setSent(true); setCooldown(60);
      toast.success('Code sent on WhatsApp');
    } catch (err) { toast.error(errText(err, 'Could not send the code — try again in a minute')); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (otp.trim().length < 4) return;
    setBusy(true);
    try {
      await api.post('/api/auth/signup-verify-phone', { phone: e164, otp: otp.trim() });
      toast.success('Number verified — welcome!');
      await refreshUser();
    } catch (err) {
      const d = err?.response?.data?.detailObject;
      if (d?.code === 'PHONE_IN_USE') { setInUse(d); setSent(false); setOtp(''); return; }
      toast.error(errText(err, 'Incorrect or expired code'));
    }
    finally { setBusy(false); }
  };

  const switchAccount = () => { logout(); window.location.assign('/login'); };

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md z-[60]" hideClose data-testid="phone-verify-gate" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5 text-rose-500" /> Verify your mobile number</DialogTitle>
          <DialogDescription>
            A one-time WhatsApp code keeps your account and class reminders safe. You'll only do this once.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {inUse && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-2" data-testid="phone-in-use-panel">
              <p className="font-medium">This number belongs to another account{inUse.owner?.email_masked ? ` (${inUse.owner.email_masked})` : ''}.</p>
              <p className="text-xs">{inUse.message}</p>
              <div className="flex gap-2 pt-1">
                <Button size="sm" variant="outline" className="rounded-full" onClick={switchAccount} data-testid="phone-in-use-switch-account">Sign in to that account</Button>
                <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setInUse(null)} data-testid="phone-in-use-try-another">Use a different number</Button>
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="pv-phone">Mobile number</Label>
            <div className="flex gap-2">
              <Input id="pv-phone" inputMode="tel" placeholder="10-digit number or +country code" value={phone} disabled={sent}
                onChange={(e) => setPhone(e.target.value)} data-testid="phone-verify-input" />
              {sent && <Button variant="ghost" size="sm" onClick={() => { setSent(false); setOtp(''); }} data-testid="phone-verify-change">Change</Button>}
            </div>
            {!sent && <p className="text-[11px] text-muted-foreground">Wrong number on file? Just type the right one — we'll verify and update it.</p>}
          </div>
          {!sent ? (
            <Button className="w-full rounded-full bg-rose-500 hover:bg-rose-600" onClick={send} disabled={busy || !e164} data-testid="phone-verify-send">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send WhatsApp code'}
            </Button>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="pv-otp">Enter the 6-digit code sent to {e164}</Label>
                <Input id="pv-otp" inputMode="numeric" maxLength={8} value={otp} onChange={(e) => setOtp(e.target.value)} data-testid="phone-verify-otp" />
              </div>
              <Button className="w-full rounded-full bg-rose-500 hover:bg-rose-600" onClick={verify} disabled={busy || otp.trim().length < 4} data-testid="phone-verify-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-1" /> Verify & continue</>}
              </Button>
              <button type="button" className="text-xs text-muted-foreground underline underline-offset-2 mx-auto block disabled:opacity-50" onClick={send} disabled={busy || cooldown > 0} data-testid="phone-verify-resend">
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
